import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash2, Copy, RotateCcw, Rocket, Save, X,
  LayoutTemplate, Users, Coffee, Video, Building2, Home, Sparkles, Archive,
} from "lucide-react";

const ROOM_TYPES = [
  { id: "reception", label: "Reception" },
  { id: "open_workspace", label: "Open Workspace" },
  { id: "department", label: "Department Area" },
  { id: "meeting", label: "Meeting Room" },
  { id: "focus", label: "Focus Room" },
  { id: "project", label: "Project Room" },
  { id: "private", label: "Private Office" },
  { id: "lounge", label: "Lounge" },
  { id: "break", label: "Break Room" },
  { id: "training", label: "Training Room" },
  { id: "event", label: "Event Space" },
  { id: "custom", label: "Custom Room" },
];
const SEAT_ARRANGEMENTS = [
  { id: "grid", label: "Grid" },
  { id: "rows", label: "Rows" },
  { id: "meeting_table", label: "Meeting Table" },
  { id: "circular", label: "Circular" },
  { id: "open", label: "Open Seating" },
  { id: "none", label: "No Fixed Seats" },
];
const COLOR_SWATCHES = [
  "#FFF3E4", "#E9EEFC", "#F7F7F5", "#E0F5E4", "#FEF3C7", "#F0EAFB",
  "#FDE7E4", "#FDEAEA", "#E9E9EF", "#FFEACC", "#E4F0FE", "#F4F4F5",
];

const CELL = 10;
const CANVAS_W = 1000;
const CANVAS_H = 640;

/** Deterministic next-room placement that doesn't stack. */
function nextRoomPlacement(existingRooms) {
  const w = 20;
  const h = 12;
  const startX = 4;
  const startY = 4;
  const stepX = w + 2;
  const stepY = h + 2;
  const cols = Math.max(1, Math.floor((100 - startX) / stepX));
  const idx = (existingRooms || []).length;
  const col = idx % cols;
  const row = Math.floor(idx / cols);
  return { x: startX + col * stepX, y: startY + row * stepY, w, h };
}

/** Compute a fresh room name that does not clash with existing "New Room N". */
function nextNewRoomName(existingRooms) {
  const used = new Set((existingRooms || []).map((r) => (r?.name || "").trim()));
  let i = 1;
  while (used.has(`New Room ${i}`)) i++;
  return `New Room ${i}`;
}

/**
 * Admin Office Studio — local-first editor.
 *
 * IMPORTANT: All edits go into a *local* draft state first (immediate, no server
 * round-trip). Server persistence is debounced and never blocks typing/dragging.
 * On save we sync patches; on load we hydrate from `/office/draft`. This fixes
 * the previous "characters disappear while typing" and "dragged rooms vanish"
 * regressions caused by re-fetching the whole layout after every patch.
 */
export default function OfficeStudio() {
  const nav = useNavigate();
  const { user, currentOrg, memberships } = useAuth();
  const membership = (memberships || []).find((m) => m.org_id === currentOrg?.id);
  const role = membership?.role || "";
  const canEdit = ["owner", "admin"].includes(role);

  // Server state (source of truth on load / after publish).
  const [published, setPublished] = useState(null);
  const [serverDraft, setServerDraft] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [members, setMembers] = useState([]);

  // Local editable draft mirrors server draft but never re-hydrates from an in-flight save.
  const [localDraft, setLocalDraft] = useState(null);

  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadAll = useCallback(async () => {
    const [pub, drf, hist, tpl, mem] = await Promise.all([
      api.get("/office/layout"),
      canEdit ? api.get("/office/draft").catch(() => ({ data: null })) : Promise.resolve({ data: null }),
      canEdit ? api.get("/office/history").catch(() => ({ data: { previous: null } })) : Promise.resolve({ data: { previous: null } }),
      api.get("/office/templates"),
      api.get("/orgs/current/members"),
    ]);
    setPublished(pub.data);
    setServerDraft(drf.data);
    setLocalDraft(drf.data ? deepClone(drf.data) : null);
    setPrevious(hist.data?.previous || null);
    setTemplates(tpl.data);
    setMembers(mem.data);
  }, [canEdit]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const activeLayout = previewMode
    ? published
    : (localDraft || serverDraft || published);
  const selectedRoom = useMemo(
    () => (activeLayout?.rooms || []).find((r) => r.id === selectedRoomId) || null,
    [activeLayout, selectedRoomId]
  );

  // ---- Debounced server sync for room patches ---------------------------
  const pendingPatchesRef = useRef(new Map()); // roomId → { patch }
  const flushTimerRef = useRef(null);

  const scheduleFlush = useCallback(() => {
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    flushTimerRef.current = setTimeout(async () => {
      const entries = Array.from(pendingPatchesRef.current.entries());
      pendingPatchesRef.current.clear();
      for (const [roomId, { patch }] of entries) {
        try {
          await api.patch(`/office/draft/rooms/${roomId}`, patch);
        } catch (e) {
          if (process.env.NODE_ENV === "development") console.debug("[studio] room patch failed", e);
          toast.error("Failed to save room change");
        }
      }
    }, 400);
  }, []);

  // Local-first room mutation. Immediately updates the on-screen state, then
  // queues a debounced backend patch.
  const patchRoomLocal = useCallback((roomId, patch) => {
    setLocalDraft((cur) => {
      if (!cur) return cur;
      const next = { ...cur, rooms: cur.rooms.map((r) => r.id === roomId ? { ...r, ...patch } : r) };
      return next;
    });
    const merged = { ...(pendingPatchesRef.current.get(roomId)?.patch || {}), ...patch };
    pendingPatchesRef.current.set(roomId, { patch: merged });
    scheduleFlush();
  }, [scheduleFlush]);

  if (!canEdit) {
    return (
      <div className="flex-1 flex items-center justify-center p-8" data-testid="office-studio-forbidden">
        <div className="max-w-md text-center">
          <div className="text-lg font-semibold mb-1">Admins only</div>
          <div className="text-sm text-zinc-500">Office Studio is available to Owners and Admins. Ask your workspace owner for access.</div>
          <Button variant="outline" className="mt-4" onClick={() => nav("/app/office")}>
            <ArrowLeft className="w-4 h-4 mr-1"/> Back to office
          </Button>
        </div>
      </div>
    );
  }

  const startDraft = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/office/draft");
      setServerDraft(data);
      setLocalDraft(deepClone(data));
      setSelectedRoomId(data.rooms?.[0]?.id || null);
      toast.success("Draft created from published layout");
    } catch { toast.error("Could not create draft"); }
    finally { setBusy(false); }
  };

  const applyTemplate = async (tplId) => {
    if (localDraft && !window.confirm("Replace the current draft with this template? Unpublished edits will be lost.")) return;
    try {
      const { data } = await api.post("/office/draft/apply-template", { template_id: tplId });
      setServerDraft(data);
      setLocalDraft(deepClone(data));
      setSelectedRoomId(data.rooms?.[0]?.id || null);
      toast.success("Template applied to draft");
    } catch { toast.error("Failed"); }
  };

  const discardDraft = async () => {
    if (!window.confirm("Discard all unpublished changes?")) return;
    try {
      await api.delete("/office/draft");
      setServerDraft(null);
      setLocalDraft(null);
      setSelectedRoomId(null);
      toast.success("Draft discarded");
    } catch { toast.error("Failed"); }
  };

  const publishDraft = async () => {
    if (!window.confirm("Publish this layout? The live Virtual Office will update for everyone.")) return;
    // Ensure pending patches land on the server before publish.
    if (flushTimerRef.current) { clearTimeout(flushTimerRef.current); flushTimerRef.current = null; }
    const entries = Array.from(pendingPatchesRef.current.entries());
    pendingPatchesRef.current.clear();
    for (const [rid, { patch }] of entries) {
      try { await api.patch(`/office/draft/rooms/${rid}`, patch); }
      catch (e) { if (process.env.NODE_ENV === "development") console.debug("[studio] pre-publish flush", e); }
    }
    setBusy(true);
    try {
      const { data } = await api.post("/office/publish");
      setPublished(data);
      setServerDraft(null);
      setLocalDraft(null);
      setSelectedRoomId(null);
      const hist = await api.get("/office/history");
      setPrevious(hist.data?.previous || null);
      toast.success(`Published version ${data.version}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally { setBusy(false); }
  };

  const restorePrevious = async () => {
    if (!previous) return;
    if (!window.confirm("Restore the previous published layout? Current draft will be discarded.")) return;
    try {
      const { data } = await api.post("/office/restore");
      setPublished(data);
      setServerDraft(null);
      setLocalDraft(null);
      setPrevious(null);
      toast.success("Restored previous version");
    } catch { toast.error("Failed"); }
  };

  const addRoom = async () => {
    if (!localDraft) return startDraft();
    const placement = nextRoomPlacement(localDraft.rooms);
    const name = nextNewRoomName(localDraft.rooms);
    const room = { name, type: "open_workspace", color: "#F7F7F5",
      x: placement.x, y: placement.y, w: placement.w, h: placement.h,
      seat_count: 4, seat_arrangement: "grid" };
    try {
      const { data } = await api.post("/office/draft/rooms", room);
      setLocalDraft((cur) => cur ? { ...cur, rooms: [...cur.rooms, data] } : cur);
      setServerDraft((cur) => cur ? { ...cur, rooms: [...cur.rooms, data] } : cur);
      setSelectedRoomId(data.id);
    } catch { toast.error("Failed"); }
  };

  const deleteRoom = async (roomId) => {
    if (!window.confirm("Delete this room from the draft?")) return;
    try {
      await api.delete(`/office/draft/rooms/${roomId}`);
      setLocalDraft((cur) => cur ? { ...cur, rooms: cur.rooms.filter((r) => r.id !== roomId) } : cur);
      setServerDraft((cur) => cur ? { ...cur, rooms: cur.rooms.filter((r) => r.id !== roomId) } : cur);
      if (selectedRoomId === roomId) {
        setSelectedRoomId((localDraft?.rooms || []).filter((r) => r.id !== roomId)[0]?.id || null);
      }
    } catch { toast.error("Failed"); }
  };

  const duplicateRoom = async (roomId) => {
    try {
      const { data } = await api.post(`/office/draft/rooms/${roomId}/duplicate`);
      setLocalDraft((cur) => cur ? { ...cur, rooms: [...cur.rooms, data] } : cur);
      setServerDraft((cur) => cur ? { ...cur, rooms: [...cur.rooms, data] } : cur);
      setSelectedRoomId(data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const assignSeat = async (roomId, seatId, patch) => {
    // Optimistic local update
    setLocalDraft((cur) => {
      if (!cur) return cur;
      return {
        ...cur,
        rooms: cur.rooms.map((r) => r.id === roomId ? {
          ...r,
          seats: (r.seats || []).map((s) => s.id === seatId ? { ...s, ...patch } : s),
        } : r),
      };
    });
    try { await api.patch(`/office/draft/rooms/${roomId}/seats/${seatId}`, patch); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-zinc-50" data-testid="office-studio">
      <div className="h-14 border-b bg-white flex items-center gap-3 px-4">
        <Button size="sm" variant="ghost" onClick={() => nav("/app/office")} data-testid="studio-back">
          <ArrowLeft className="w-4 h-4 mr-1"/> Back
        </Button>
        <h1 className="text-base font-semibold flex items-center gap-2">
          <Building2 className="w-4 h-4 text-[var(--brand-accent)]"/> Office Studio
        </h1>
        <div className="ml-2 text-xs text-zinc-500">
          {activeLayout?.name} · <span className="mono">v{activeLayout?.version || 1}</span>
          {localDraft && !previewMode && <span className="ml-2 rounded-full bg-orange-100 text-orange-700 px-2 py-0.5">Draft</span>}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <label className="flex items-center gap-1 text-xs text-zinc-600 mr-2">
            <input type="checkbox" checked={previewMode} onChange={(e) => setPreviewMode(e.target.checked)} data-testid="studio-preview-toggle"/>
            Preview published
          </label>
          {previous && (
            <Button size="sm" variant="outline" onClick={restorePrevious} data-testid="studio-restore-btn">
              <Archive className="w-4 h-4 mr-1"/> Restore previous
            </Button>
          )}
          {!localDraft ? (
            <Button size="sm" onClick={startDraft} disabled={busy} data-testid="studio-start-draft">
              <Sparkles className="w-4 h-4 mr-1"/> Create draft
            </Button>
          ) : (
            <>
              <Button size="sm" variant="outline" onClick={discardDraft} disabled={busy} data-testid="studio-discard">
                <RotateCcw className="w-4 h-4 mr-1"/> Discard
              </Button>
              <Button size="sm" className="bg-[var(--brand-primary)] text-white" onClick={publishDraft} disabled={busy} data-testid="studio-publish">
                <Rocket className="w-4 h-4 mr-1"/> Publish
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="flex-1 grid grid-cols-[280px_1fr_320px] overflow-hidden">
        <aside className="border-r bg-white flex flex-col overflow-hidden">
          <div className="p-4 border-b">
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1"><LayoutTemplate className="w-3 h-3"/> Templates</div>
            <div className="space-y-1" data-testid="studio-templates">
              {templates.map((t) => (
                <button key={t.id} onClick={() => applyTemplate(t.id)}
                  className="w-full text-left p-2 rounded hover:bg-orange-50 border border-transparent hover:border-orange-100 transition"
                  data-testid={`studio-template-${t.id}`}>
                  <div className="text-sm font-medium">{t.name}</div>
                  <div className="text-[11px] text-zinc-500 leading-tight">{t.description}</div>
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-1"><Home className="w-3 h-3"/> Rooms</div>
              {localDraft && (
                <button onClick={addRoom} className="text-[var(--brand-accent)] text-xs font-medium" data-testid="studio-add-room">
                  <Plus className="w-3 h-3 inline"/> Add
                </button>
              )}
            </div>
            {(activeLayout?.rooms || []).map((r) => (
              <button key={r.id} onClick={() => setSelectedRoomId(r.id)}
                className={`w-full flex items-center gap-2 p-2 rounded text-left text-sm mb-1 transition ${selectedRoomId === r.id ? "bg-orange-50 border-l-2 border-[var(--brand-accent)]" : "hover:bg-zinc-50"}`}
                data-testid={`studio-room-item-${r.id}`}>
                <span className="w-3 h-3 rounded" style={{ background: r.color }}/>
                <span className="flex-1 truncate">{r.name}</span>
                <span className="text-[10px] text-zinc-400">{r.type}</span>
              </button>
            ))}
            {!localDraft && !previewMode && (
              <div className="text-xs text-zinc-500 mt-4 p-2 border border-dashed rounded">
                You&apos;re viewing the live published layout. Click &quot;Create draft&quot; to edit.
              </div>
            )}
          </div>
        </aside>

        <div className="overflow-auto scroll-thin p-6 flex items-start justify-center">
          <div className="relative bg-white rounded-lg shadow-sm border overflow-hidden"
            style={{ width: CANVAS_W, height: CANVAS_H, backgroundImage: "linear-gradient(0deg, #e5e7eb 1px, transparent 1px), linear-gradient(90deg, #e5e7eb 1px, transparent 1px)", backgroundSize: `${CELL}px ${CELL}px` }}
            data-testid="studio-canvas">
            {(activeLayout?.rooms || []).map((r) => (
              <StudioRoom
                key={r.id}
                room={r}
                selected={selectedRoomId === r.id && !previewMode}
                editable={!!localDraft && !previewMode}
                onSelect={() => setSelectedRoomId(r.id)}
                onMove={(patch) => patchRoomLocal(r.id, patch)}
              />
            ))}
          </div>
        </div>

        <aside className="border-l bg-white overflow-y-auto scroll-thin" data-testid="studio-properties">
          {selectedRoom ? (
            <RoomProperties
              key={selectedRoom.id}
              room={selectedRoom}
              members={members}
              editable={!!localDraft && !previewMode}
              onChange={(p) => patchRoomLocal(selectedRoom.id, p)}
              onDelete={() => deleteRoom(selectedRoom.id)}
              onDuplicate={() => duplicateRoom(selectedRoom.id)}
              onSeatChange={(seatId, patch) => assignSeat(selectedRoom.id, seatId, patch)}
              isDefaultSpawn={activeLayout?.default_spawn_room_id === selectedRoom.id}
              onSetDefaultSpawn={async () => {
                try {
                  await api.patch("/office/draft", { default_spawn_room_id: selectedRoom.id });
                  setLocalDraft((cur) => cur ? { ...cur, default_spawn_room_id: selectedRoom.id } : cur);
                } catch { toast.error("Failed"); }
              }}
            />
          ) : (
            <div className="p-6 text-sm text-zinc-500 text-center">
              Select a room from the list or canvas to edit its properties.
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function deepClone(x) { return JSON.parse(JSON.stringify(x)); }

/* ---------------- Studio room rendering (drag + resize) ---------------- */

function StudioRoom({ room, selected, editable, onSelect, onMove }) {
  const px = { left: room.x * CELL, top: room.y * CELL, width: room.w * CELL, height: room.h * CELL };
  const ref = useRef(null);

  // Closure-based drag: no useState / useEffect involved, so a pure click
  // does not trigger any re-render dance that can strand style overrides on
  // the DOM node. A drag only "activates" after the pointer travels past a
  // small threshold, so clicks stay clicks and drags stay drags.
  const onPointerDown = (e, mode = "move") => {
    if (!editable) { onSelect(); return; }
    e.stopPropagation();
    onSelect();
    const startX = e.clientX;
    const startY = e.clientY;
    const origin = { x: room.x, y: room.y, w: room.w, h: room.h };
    let activated = false;
    const THRESHOLD_PX = 4;

    const onMoveEvt = (ev) => {
      if (!activated) {
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        if (dx * dx + dy * dy < THRESHOLD_PX * THRESHOLD_PX) return;
        activated = true;
      }
      const dxCells = Math.round((ev.clientX - startX) / CELL);
      const dyCells = Math.round((ev.clientY - startY) / CELL);
      if (!ref.current) return;
      if (mode === "move") {
        ref.current.style.transform = `translate(${dxCells * CELL}px, ${dyCells * CELL}px)`;
      } else {
        ref.current.style.width = `${Math.max(6, origin.w + dxCells) * CELL}px`;
        ref.current.style.height = `${Math.max(6, origin.h + dyCells) * CELL}px`;
      }
    };

    const onUp = (ev) => {
      window.removeEventListener("pointermove", onMoveEvt);
      window.removeEventListener("pointerup", onUp);
      if (ref.current) {
        ref.current.style.transform = "";
        ref.current.style.width = "";
        ref.current.style.height = "";
      }
      // Pure click → nothing else to do (selection already committed).
      if (!activated) return;
      const dxCells = Math.round((ev.clientX - startX) / CELL);
      const dyCells = Math.round((ev.clientY - startY) / CELL);
      if (dxCells === 0 && dyCells === 0) return;
      if (mode === "move") {
        const maxCol = Math.max(0, Math.floor(CANVAS_W / CELL) - origin.w);
        const maxRow = Math.max(0, Math.floor(CANVAS_H / CELL) - origin.h);
        onMove({
          x: Math.max(0, Math.min(maxCol, origin.x + dxCells)),
          y: Math.max(0, Math.min(maxRow, origin.y + dyCells)),
        });
      } else {
        onMove({ w: Math.max(6, origin.w + dxCells), h: Math.max(6, origin.h + dyCells) });
      }
    };

    window.addEventListener("pointermove", onMoveEvt);
    window.addEventListener("pointerup", onUp);
  };

  return (
    <div
      ref={ref}
      onPointerDown={onPointerDown}
      className={`absolute rounded-lg overflow-hidden border-2 transition-shadow ${editable ? "cursor-move" : "cursor-pointer"} ${selected ? "border-[var(--brand-accent)] shadow-lg ring-2 ring-orange-200" : "border-zinc-200"}`}
      style={{ ...px, background: room.color, touchAction: "none", zIndex: selected ? 5 : 1 }}
      data-testid={`studio-room-${room.id}`}
    >
      <div className="absolute inset-0 p-2 flex flex-col pointer-events-none">
        <div className="text-[11px] font-semibold text-zinc-700 truncate">{room.name}</div>
        <div className="text-[10px] text-zinc-500">{room.seat_count || 0} seats · {ROOM_TYPES.find((t) => t.id === room.type)?.label || room.type}</div>
      </div>
      {(room.seats || []).map((s) => (
        <div key={s.id} className="absolute -translate-x-1/2 -translate-y-1/2"
          style={{ left: `${(s.x || 0.5) * 100}%`, top: `${(s.y || 0.5) * 100}%` }}
          title={s.assigned_user_id ? "Assigned" : (s.hot ? "Hot desk" : "Open seat")}>
          <div className={`w-3 h-3 rounded-full border-2 ${s.assigned_user_id ? "bg-[var(--brand-accent)] border-white" : s.hot ? "bg-white border-zinc-500" : "bg-zinc-300 border-white"}`}/>
        </div>
      ))}
      {editable && selected && (
        <div onPointerDown={(e) => onPointerDown(e, "resize")}
          className="absolute right-0 bottom-0 w-3 h-3 bg-[var(--brand-accent)] rounded-tl cursor-nwse-resize"
          data-testid={`studio-resize-${room.id}`}/>
      )}
    </div>
  );
}

/* ---------------- Room properties panel (fully controlled local inputs) ---------------- */

function RoomProperties({ room, members, editable, onChange, onDelete, onDuplicate, onSeatChange, isDefaultSpawn, onSetDefaultSpawn }) {
  // Capture current onChange in a ref so effects that "commit on debounce" do
  // not re-fire on every parent rerender.
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  const set = useCallback((k, v) => onChangeRef.current({ [k]: v }), []);

  // Name is a fully controlled local input — commits to the draft on debounce
  // (250ms) and immediately on blur.
  const [nameLocal, setNameLocal] = useState(room.name || "");
  useEffect(() => { setNameLocal(room.name || ""); }, [room.id, room.name]);
  useEffect(() => {
    if (!editable) return;
    if (nameLocal === room.name) return;
    const t = setTimeout(() => set("name", nameLocal), 250);
    return () => clearTimeout(t);
  }, [nameLocal, editable, room.name, set]);

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase text-zinc-500">Room name</div>
          <Input value={nameLocal} disabled={!editable}
            onChange={(e) => setNameLocal(e.target.value)}
            onBlur={() => { if (editable && nameLocal !== room.name) set("name", nameLocal); }}
            placeholder="e.g. Finance Discussion Room"
            className="text-base font-semibold border-0 px-0" data-testid="studio-room-name"/>
        </div>
        {editable && (
          <div className="flex gap-1 ml-2">
            <button onClick={onDuplicate} className="text-zinc-500 hover:text-zinc-800" title="Duplicate" data-testid="studio-room-duplicate"><Copy className="w-4 h-4"/></button>
            <button onClick={onDelete} className="text-red-500 hover:text-red-700" title="Delete" data-testid="studio-room-delete"><Trash2 className="w-4 h-4"/></button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs uppercase text-zinc-500">Type</label>
          <Select value={room.type} onValueChange={(v) => set("type", v)} disabled={!editable}>
            <SelectTrigger data-testid="studio-room-type"><SelectValue/></SelectTrigger>
            <SelectContent>{ROOM_TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs uppercase text-zinc-500">Department</label>
          <Input defaultValue={room.department || ""} disabled={!editable}
            onBlur={(e) => set("department", e.target.value || null)}
            placeholder="Optional" data-testid="studio-room-department"/>
        </div>
      </div>

      <div>
        <label className="text-xs uppercase text-zinc-500">Color</label>
        <div className="flex flex-wrap gap-1 mt-1" data-testid="studio-room-color">
          {COLOR_SWATCHES.map((c) => (
            <button key={c} disabled={!editable} onClick={() => set("color", c)}
              className={`w-6 h-6 rounded border-2 ${room.color?.toUpperCase() === c.toUpperCase() ? "border-[var(--brand-accent)] scale-110" : "border-zinc-200"} transition`}
              style={{ background: c }}/>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs uppercase text-zinc-500">Capacity</label>
          <Input type="number" min={1} max={60} defaultValue={room.capacity ?? 6} disabled={!editable}
            onBlur={(e) => set("capacity", Math.max(1, Math.min(60, Number(e.target.value))))}
            data-testid="studio-room-capacity"/>
        </div>
        <div>
          <label className="text-xs uppercase text-zinc-500">Seats</label>
          <Input type="number" min={0} max={24} defaultValue={room.seat_count ?? 0} disabled={!editable}
            onBlur={(e) => set("seat_count", Math.max(0, Math.min(24, Number(e.target.value))))}
            data-testid="studio-room-seat-count"/>
        </div>
      </div>

      <div>
        <label className="text-xs uppercase text-zinc-500">Seat arrangement</label>
        <Select value={room.seat_arrangement || "grid"} onValueChange={(v) => set("seat_arrangement", v)} disabled={!editable}>
          <SelectTrigger data-testid="studio-room-arrangement"><SelectValue/></SelectTrigger>
          <SelectContent>{SEAT_ARRANGEMENTS.map((a) => <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs uppercase text-zinc-500">Grid X</label>
          <Input type="number" defaultValue={room.x} disabled={!editable} onBlur={(e) => set("x", Math.max(0, Number(e.target.value)))}/>
        </div>
        <div>
          <label className="text-xs uppercase text-zinc-500">Grid Y</label>
          <Input type="number" defaultValue={room.y} disabled={!editable} onBlur={(e) => set("y", Math.max(0, Number(e.target.value)))}/>
        </div>
        <div>
          <label className="text-xs uppercase text-zinc-500">Width</label>
          <Input type="number" defaultValue={room.w} disabled={!editable} onBlur={(e) => set("w", Math.max(6, Number(e.target.value)))}/>
        </div>
        <div>
          <label className="text-xs uppercase text-zinc-500">Height</label>
          <Input type="number" defaultValue={room.h} disabled={!editable} onBlur={(e) => set("h", Math.max(6, Number(e.target.value)))}/>
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" disabled={!editable} checked={!!room.huddle_enabled}
          onChange={(e) => set("huddle_enabled", e.target.checked)}
          data-testid="studio-huddle-toggle"/>
        <Video className="w-4 h-4 text-zinc-500"/> Enable huddles in this room
      </label>

      <RestrictionsEditor room={room} disabled={!editable} onChange={(rest) => set("restrictions", rest)}/>

      <div className="pt-2">
        <Button size="sm" variant={isDefaultSpawn ? "default" : "outline"} disabled={!editable || isDefaultSpawn}
          onClick={onSetDefaultSpawn} data-testid="studio-set-default-spawn">
          {isDefaultSpawn ? "Default spawn room ✓" : "Set as default spawn room"}
        </Button>
      </div>

      {(room.seats || []).length > 0 && (
        <div>
          <div className="text-xs uppercase text-zinc-500 mb-2 flex items-center gap-1"><Users className="w-3 h-3"/> Seat assignments</div>
          <div className="space-y-1" data-testid="studio-seats">
            {room.seats.map((s, i) => (
              <div key={s.id} className="flex items-center gap-2 text-xs">
                <span className="w-5 text-zinc-400 mono">#{i + 1}</span>
                <Select value={s.assigned_user_id || "__none__"} disabled={!editable}
                  onValueChange={(v) => onSeatChange(s.id, { assigned_user_id: v === "__none__" ? null : v })}>
                  <SelectTrigger className="h-7 flex-1"><SelectValue placeholder="Unassigned"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Unassigned —</SelectItem>
                    {members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.user?.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <label className="flex items-center gap-1">
                  <input type="checkbox" disabled={!editable} checked={!!s.hot}
                    onChange={(e) => onSeatChange(s.id, { hot: e.target.checked })}
                    data-testid={`studio-seat-hot-${s.id}`}/>
                  Hot
                </label>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RestrictionsEditor({ room, disabled, onChange }) {
  const roles = room.restrictions?.roles || [];
  const deptIds = room.restrictions?.department_ids || [];
  const legacyDeptNames = room.restrictions?.departments || [];
  const [departments, setDepartments] = useState([]);
  const [deptSearch, setDeptSearch] = useState("");
  const [deptOpen, setDeptOpen] = useState(false);

  useEffect(() => {
    let cancel = false;
    api.get("/departments").then(({ data }) => { if (!cancel) setDepartments(data || []); }).catch(() => {});
    return () => { cancel = true; };
  }, []);

  const toggle = (list, val) => list.includes(val) ? list.filter((x) => x !== val) : [...list, val];
  const emit = (nextRoles, nextDeptIds) => onChange({
    ...room.restrictions,
    roles: nextRoles,
    department_ids: nextDeptIds,
    // preserve legacy free-text names so old published layouts keep matching.
    departments: legacyDeptNames,
  });
  const filteredDepts = departments.filter((d) =>
    d.active && !deptIds.includes(d.id) &&
    (deptSearch ? d.name.toLowerCase().includes(deptSearch.toLowerCase()) : true)
  );

  const nameForId = (id) => departments.find((d) => d.id === id)?.name || "…";

  return (
    <div>
      <label className="text-xs uppercase text-zinc-500">Access restrictions</label>
      <div className="mt-1 space-y-2">
        <div className="flex flex-wrap gap-1" data-testid="studio-restrict-roles">
          {["owner", "admin", "manager", "member"].map((r) => (
            <button key={r} disabled={disabled} onClick={() => emit(toggle(roles, r), deptIds)}
              className={`text-[11px] rounded-full px-2 py-0.5 border ${roles.includes(r) ? "border-[var(--brand-accent)] bg-orange-50" : "border-zinc-200"}`}
              data-testid={`studio-restrict-role-${r}`}>{r}</button>
          ))}
        </div>
        <div className="relative">
          <div className="flex flex-wrap gap-1 items-center min-h-[28px] p-1 border rounded" data-testid="studio-restrict-departments">
            {deptIds.map((id) => (
              <span key={id} className="text-[11px] rounded-full bg-orange-50 border border-orange-200 px-2 py-0.5" data-testid={`studio-restrict-dept-${id}`}>
                {nameForId(id)}
                <button disabled={disabled} onClick={() => emit(roles, deptIds.filter((x) => x !== id))} className="ml-1 text-zinc-500">×</button>
              </span>
            ))}
            <Input
              value={deptSearch}
              onChange={(e) => { setDeptSearch(e.target.value); setDeptOpen(true); }}
              onFocus={() => setDeptOpen(true)}
              onBlur={() => setTimeout(() => setDeptOpen(false), 150)}
              placeholder={deptIds.length ? "Add another…" : "Search departments…"}
              className="h-6 text-xs border-0 flex-1 min-w-[100px] p-1"
              disabled={disabled}
              data-testid="studio-restrict-dept-search"
            />
          </div>
          {deptOpen && !disabled && filteredDepts.length > 0 && (
            <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border rounded shadow-md max-h-40 overflow-y-auto" data-testid="studio-restrict-dept-menu">
              {filteredDepts.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { emit(roles, [...deptIds, d.id]); setDeptSearch(""); }}
                  className="w-full text-left px-2 py-1 text-xs hover:bg-orange-50"
                  data-testid={`studio-restrict-dept-option-${d.id}`}
                >
                  {d.name}
                  {d.member_count > 0 && <span className="ml-1 text-zinc-400">· {d.member_count} members</span>}
                </button>
              ))}
            </div>
          )}
          {deptOpen && !disabled && filteredDepts.length === 0 && (
            <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border rounded shadow-md px-2 py-2 text-[11px] text-zinc-500">
              {departments.length === 0
                ? <>No departments yet. <Link to="/app/departments" className="text-[var(--brand-accent)]">Create one →</Link></>
                : "No matching active departments."}
            </div>
          )}
        </div>
        {legacyDeptNames.length > 0 && (
          <div className="text-[10px] text-zinc-400" data-testid="studio-restrict-legacy">
            Legacy department names on this room: {legacyDeptNames.join(", ")} — kept for backward-compat.
          </div>
        )}
        <div className="text-[10px] text-zinc-400">
          Members without a listed role or department cannot enter this room. Owners always retain access.
        </div>
      </div>
    </div>
  );
}
